import * as React from "react";

type PresenceState = "open" | "closing" | "unmounted";

const EXIT_FALLBACK_MS = 150;

function prefersReducedMotion() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Tracks mount-through-exit lifecycle for an animatable element.
 * Stays mounted during exit animation; unmounts on transitionend/animationend
 * or after a 150ms fallback (collapsed to 0 under prefers-reduced-motion).
 */
export function usePresence(open: boolean) {
  const [state, setState] = React.useState<PresenceState>(open ? "open" : "unmounted");
  const ref = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (open) {
      setState("open");
      return;
    }
    if (state === "unmounted") return;
    setState("closing");

    const node = ref.current;
    const duration = prefersReducedMotion() ? 0 : EXIT_FALLBACK_MS;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setState("unmounted");
    };

    const timer = window.setTimeout(finish, duration + 20);
    const handler = (e: Event) => {
      if (e.target === node) finish();
    };
    node?.addEventListener("transitionend", handler);
    node?.addEventListener("animationend", handler);

    return () => {
      window.clearTimeout(timer);
      node?.removeEventListener("transitionend", handler);
      node?.removeEventListener("animationend", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mounted = state !== "unmounted";
  const dataState = state === "open" ? "open" : "closed";

  return { mounted, state, dataState, ref } as const;
}

interface PresenceProps {
  open: boolean;
  children: (args: {
    ref: React.RefCallback<HTMLElement>;
    dataState: "open" | "closed";
    dataProps: {
      "data-state": "open" | "closed";
      "data-open"?: "";
      "data-closed"?: "";
    };
  }) => React.ReactNode;
}

/**
 * Render-prop helper for <Presence>. Emits both `data-state` and bare
 * `data-open`/`data-closed` attrs so existing CSS read-paths keep working.
 */
export function Presence({ open, children }: PresenceProps) {
  const { mounted, dataState, ref } = usePresence(open);
  if (!mounted) return null;

  const setRef: React.RefCallback<HTMLElement> = (node) => {
    ref.current = node;
  };
  const dataProps =
    dataState === "open"
      ? ({ "data-state": "open", "data-open": "" } as const)
      : ({ "data-state": "closed", "data-closed": "" } as const);

  return <>{children({ ref: setRef, dataState, dataProps })}</>;
}
